import { useCallback, useMemo, useRef, useState } from "react";

import { noop } from "lodash";

import { useTranslate } from "@similarweb/i18n-provider";
import { LineGraph } from "@similarweb/pro-logic-components";
import { LegendWithOneLineCheckboxFlex } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { Legends } from "app/components/React/Legends/Legends";
import { EGridDirections } from "app/components/React/Legends/legendsTypes";
import { DownloadButtonMenu } from "app/components/React/DownloadButtonMenu/DownloadButtonMenu";
import { WeShare } from "app/components/weshare/weshare";
import { minVisitsAbbrFilter } from "app/filters/ngFilters";
import { ConversionRateChartEmptyState } from "./ConversionRateChartEmptyState";
import { TimeGranularitySwitcher } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/TimeGranularitySwitcher";
import {
    ChartContainer,
    ButtonsContainer,
    CheckboxesContainer,
    ChartSection,
    UtilitiesContainer,
} from "app/pages/website-analysis/TrafficAndEngagement/Components/StyledComponents";
import { PopSingleModeLegends } from "app/pages/website-analysis/TrafficAndEngagement/Legends/PopSingleModeLegends";
import { pngDownload } from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import DurationService from "app/services/DurationService";
import { PngHeader } from "app/UtilitiesAndConstants/UtilitiesComponents/PngHeader";
import { GraphLoader } from "pro-features/components/Loaders/src/ExpandedTableRowLoader/ExpandedTableRowLoader";

import type { ConversionRatesProps } from "./types";
import { useConversionRatesChart } from "./useConversionRatesChart";

const abbreviateNumber = minVisitsAbbrFilter();

const MONTHLY_GRANULARITY = { name: "Monthly" };
const GRANULARITY_OPTIONS = [
    { title: "D", name: "Daily", disabled: true },
    { title: "W", name: "Weekly", disabled: true },
    { title: "M", name: "Monthly", disabled: false },
];
const getGranularity = () => GRANULARITY_OPTIONS;

export const ConversionRates = (props: ConversionRatesProps) => {
    const { params, meta, chosenSites } = props;
    const chartPngRef = useRef<HTMLDivElement | null>(null);
    const [hiddenSites, setHiddenSites] = useState<Set<string>>(() => new Set());
    const isCompareMode = meta.isSingleMode === false;

    const { query, graphSeries, graphConfig, hasData, popLegendItems, siteAverages, emptyStateType } =
        useConversionRatesChart({ ...props, hiddenSites });
    const { translate } = useTranslate();
    const chartTitle = translate("wa.ao.graph.converted.visits");
    const durations = useMemo(
        () => DurationService.getDurationData(params.duration, params.comparedDuration),
        [params.duration, params.comparedDuration],
    );

    const compareLegendItems = useMemo(() => {
        if (!isCompareMode) {
            return [];
        }

        return graphSeries.map((series) => {
            const siteAvg = siteAverages[series.name] ?? 0;

            return {
                name: series.name,
                color: series.color,
                hidden: hiddenSites.has(series.name),
                ...(meta.isPOP ? {} : { data: abbreviateNumber(siteAvg) }),
            };
        });
    }, [isCompareMode, meta.isPOP, graphSeries, siteAverages, hiddenSites]);

    const toggleSeries = useCallback((legendItem: { name: string }, hidden: boolean) => {
        setHiddenSites((prev) => {
            const next = new Set(prev);
            if (hidden) {
                next.delete(String(legendItem.name));
            } else {
                next.add(String(legendItem.name));
            }
            return next;
        });
    }, []);

    const renderChartContent = () => {
        if (query.isLoading) {
            return (
                <GraphLoader
                    width={window.innerWidth > 1440 ? 1280 : 1000}
                    height={288}
                />
            );
        }

        if (query.isError || !hasData) {
            return <ConversionRateChartEmptyState emptyStateType={emptyStateType} />;
        }

        const chart = (
            <Box width="100%">
                <LineGraph
                    withMarkers
                    data={graphSeries}
                    shouldPresentLegend={false}
                    graphConfig={graphConfig}
                />
            </Box>
        );

        if (isCompareMode) {
            return (
                <Flex>
                    <ChartContainer>{chart}</ChartContainer>
                    <CheckboxesContainer>
                        <Box padding="0 10px 0 0">
                            <Legends
                                legendItems={compareLegendItems}
                                toggleSeries={toggleSeries}
                                gridDirection={EGridDirections.COLUMN}
                                legendsGap="8px"
                                legendComponent={LegendWithOneLineCheckboxFlex}
                                textMaxWidth="150px"
                            />
                        </Box>
                    </CheckboxesContainer>
                </Flex>
            );
        }

        if (!meta.isPOP) {
            return <Flex>{chart}</Flex>;
        }

        return (
            <Flex>
                <ChartContainer>{chart}</ChartContainer>
                <CheckboxesContainer>
                    <Box padding="0 10px 0 0">
                        <PopSingleModeLegends
                            legendItems={popLegendItems}
                            webSource={params.webSource || "Total"}
                            chartType="line"
                            customLegends={true}
                        />
                    </Box>
                </CheckboxesContainer>
            </Flex>
        );
    };

    return (
        <div
            ref={chartPngRef}
            data-automation="conversion-rates-chart"
        >
            <PngHeader
                metricTitle={chartTitle}
                durations={durations.forWidget}
                webSource={params.webSource || "Total"}
                country={Number(params.country) || 999}
            />
            <UtilitiesContainer legendsAmount={1}>
                <ButtonsContainer legendsAmount={1}>
                    <TimeGranularitySwitcher
                        timeGranularity={MONTHLY_GRANULARITY}
                        granularityUpdate={noop}
                        getGranularity={getGranularity}
                    />
                    <DownloadButtonMenu
                        PNG={true}
                        handleDownloadFunction={() => {
                            if (chartPngRef.current) {
                                return pngDownload(chartPngRef.current, chartTitle);
                            }

                            return Promise.resolve();
                        }}
                    />
                    <WeShare
                        chartRef={chartPngRef.current}
                        description={chartTitle}
                        title={`${chosenSites?.[0] || "website"} ${chartTitle}`}
                        redirectUrl={`https://www.similarweb.com/website/${chosenSites?.[0] || ""}/#overview`}
                        isHighChart={false}
                    />
                </ButtonsContainer>
            </UtilitiesContainer>
            <ChartSection>{renderChartContent()}</ChartSection>
        </div>
    );
};
